import type { DashboardProductListItem } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  getPromotionProductsByIds,
  searchPromotionProducts,
} from '../../data/fetchers';

const PAGE_SIZE = 20;

export interface PromotionProductSelectorProps {
  selectedIds: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}

export function PromotionProductSelector({
  selectedIds,
  disabled,
  onChange,
}: PromotionProductSelectorProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [resultsOpen, setResultsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [knownProducts, setKnownProducts] = useState<
    Record<string, DashboardProductListItem>
  >({});
  const normalizedSelectedIds = useMemo(
    () => [...new Set(selectedIds)],
    [selectedIds],
  );
  const hydrationKey = useMemo(
    () => [...normalizedSelectedIds].sort(),
    [normalizedSelectedIds],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!resultsOpen) return;

    const closeWhenPressedOutside = (event: PointerEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setResultsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeWhenPressedOutside);
    return () =>
      document.removeEventListener('pointerdown', closeWhenPressedOutside);
  }, [resultsOpen]);

  const searchQuery = useQuery({
    queryKey: ['promotion-product-search', debouncedSearch, page],
    queryFn: () => searchPromotionProducts(debouncedSearch, page),
    enabled: debouncedSearch.length > 0,
  });
  const hydrationQuery = useQuery({
    queryKey: ['promotion-product-hydration', hydrationKey],
    queryFn: () => getPromotionProductsByIds(hydrationKey),
    enabled: hydrationKey.length > 0,
  });

  useEffect(() => {
    const rows = [
      ...(searchQuery.data?.items ?? []),
      ...(hydrationQuery.data ?? []),
    ];
    if (rows.length === 0) return;
    setKnownProducts((current) => {
      const next = { ...current };
      rows.forEach((row) => {
        next[row.id] = row;
      });
      return next;
    });
  }, [searchQuery.data, hydrationQuery.data]);

  const toggle = (id: string) => {
    const current = new Set(normalizedSelectedIds);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    onChange([...current]);
  };

  const total = searchQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div ref={selectorRef} className="flex flex-col gap-3">
      <div className="relative">
        <input
          type="search"
          aria-label="ค้นหาสินค้า"
          placeholder="ค้นหาชื่อสินค้า รหัสสินค้า หรือบาร์โค้ด"
          disabled={disabled}
          value={search}
          onFocus={() => setResultsOpen(true)}
          onChange={(event) => {
            setSearch(event.target.value);
            setResultsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setResultsOpen(false);
          }}
          className="h-10 w-full rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:bg-bg-warm"
        />

        {resultsOpen && !disabled && (
          <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-btn border border-line bg-surface shadow-modal">
            {debouncedSearch.length === 0 ? (
              <p className="p-3 text-sm text-ink-3">พิมพ์เพื่อค้นหาสินค้า</p>
            ) : searchQuery.isFetching ? (
              <p role="status" className="p-3 text-sm text-ink-3">
                กำลังค้นหา…
              </p>
            ) : searchQuery.isError ? (
              <div className="flex items-center justify-between gap-3 p-3">
                <p className="text-sm text-danger">ค้นหาสินค้าไม่สำเร็จ</p>
                <button
                  type="button"
                  onClick={() => void searchQuery.refetch()}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  ลองใหม่
                </button>
              </div>
            ) : (
              <>
                <p
                  role="status"
                  className="border-b border-line-2 px-3 py-2 text-xs text-ink-3"
                >
                  พบ {total} รายการ
                </p>
                {(searchQuery.data?.items.length ?? 0) === 0 ? (
                  <p className="p-3 text-sm text-ink-3">ไม่พบสินค้าที่ค้นหา</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {searchQuery.data?.items.map((product) => (
                      <label
                        key={product.id}
                        className="flex cursor-pointer items-center gap-3 border-b border-line-2 px-3 py-2.5 last:border-0 hover:bg-bg-warm/40"
                      >
                        <input
                          type="checkbox"
                          aria-label={`${product.name} ${product.baseUnitName ?? product.id}`}
                          checked={normalizedSelectedIds.includes(product.id)}
                          onChange={() => toggle(product.id)}
                          className="h-4.5 w-4.5 cursor-pointer rounded border-line accent-primary focus:ring-primary/30"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-ink-2">
                            {product.name}
                          </span>
                          <span className="block truncate text-xs text-ink-3">
                            {product.id}
                            {product.baseUnitName
                              ? ` · ${product.baseUnitName}`
                              : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {total > PAGE_SIZE && (
                  <div className="flex items-center justify-between border-t border-line-2 px-3 py-2">
                    <button
                      type="button"
                      aria-label="หน้าก่อนหน้า"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => current - 1)}
                      className="text-sm font-bold text-primary disabled:text-ink-3"
                    >
                      ก่อนหน้า
                    </button>
                    <span className="text-xs text-ink-3">
                      หน้า {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      aria-label="หน้าถัดไป"
                      disabled={page >= totalPages}
                      onClick={() => setPage((current) => current + 1)}
                      className="text-sm font-bold text-primary disabled:text-ink-3"
                    >
                      ถัดไป
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="rounded-btn border border-line-2 bg-bg-warm/30 p-3">
        <p className="text-sm font-bold text-ink-2">
          เลือกแล้ว {normalizedSelectedIds.length} รายการ
        </p>

        {hydrationQuery.isError && (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-btn bg-danger/5 p-2">
            <p className="text-xs text-danger">
              โหลดรายละเอียดสินค้าที่เลือกไม่สำเร็จ
            </p>
            <button
              type="button"
              onClick={() => void hydrationQuery.refetch()}
              className="text-xs font-bold text-primary hover:underline"
            >
              ลองโหลดสินค้าอีกครั้ง
            </button>
          </div>
        )}

        {normalizedSelectedIds.length === 0 ? (
          <p className="mt-2 text-sm text-ink-3">ยังไม่ได้เลือกสินค้า</p>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            {normalizedSelectedIds.map((id) => {
              const product = knownProducts[id];
              const unresolved =
                !product &&
                !hydrationQuery.isPending &&
                !hydrationQuery.isFetching;
              const label = product?.name ?? id;
              return (
                <div
                  key={id}
                  className="flex items-center justify-between gap-3 rounded-btn border border-line-2 bg-surface px-3 py-2"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-ink-2">
                      {label}
                    </span>
                    {product ? (
                      <span className="block truncate text-xs text-ink-3">
                        {id}
                        {product.baseUnitName
                          ? ` · ${product.baseUnitName}`
                          : ''}
                      </span>
                    ) : unresolved ? (
                      <span className="block text-xs text-danger">
                        ไม่พบสินค้า
                      </span>
                    ) : null}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      aria-label={`นำ ${label} ออก`}
                      onClick={() => toggle(id)}
                      className="shrink-0 rounded-full px-2 py-1 text-sm font-bold text-danger hover:bg-danger/10"
                    >
                      นำออก
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
